import { useIntl } from 'react-intl';

import { Box } from '@/components/Box';
import { Flex } from '@/components/Flex';
import { useCompoundContext } from '@/components/Header/context';
import LinkWithLocale from '@/components/LinkWithLocale/LinkWithLocale';
import { Typography } from '@/components/Typography';
import { useCheckout } from '@/hooks/useCheckout';
import { useRouter } from '@/hooks/useRouter';
import { useUser } from '@/hooks/useUser';
import { useWishList } from '@/hooks/useWishlist';
import { commonMessages } from '@/messages/common';
import { useRegionProvider } from '@/providers/RegionProvider';
import { searchStateActions } from '@/states/searchState';
import type { HeaderThemeKeys } from '@/theme/types/components';

import * as S from './NavigationMenu.styles';

export interface NavigationMenuProps {
  handleCartClick?: () => void;
  headerBackground: HeaderThemeKeys;
  isShrunk?: boolean;
  textColorOption?: string;
}

export const NavigationMenu = ({
  handleCartClick,
  headerBackground,
  isShrunk = true,
  textColorOption = 'dark',
}: NavigationMenuProps) => {
  const { formatMessage } = useIntl();
  const { asPath, paths } = useRouter();
  const { productCatalogMode } = useRegionProvider();
  const { country } = useRegionProvider();
  const {
    snapshot: { isProcessing, linesCount },
  } = useCheckout();
  const {
    snapshot: { isAuthenticated },
  } = useUser();
  useCompoundContext();
  const { isHighlighted } = useWishList({ countryCode: country.code });

  const isBagEmpty = linesCount === 0;
  const CartIcon = () => {
    return (
      <Flex data-test-id='NavigationMenuCartIcon'>
        <S.CartIcon
          $headerBackground={headerBackground}
          $isBagEmpty={!isBagEmpty}
          $isShrunk={isShrunk}
          $textColorOption={textColorOption}
          name='bag'
          style={{ cursor: isProcessing ? 'wait' : 'pointer' }}
        />

        <Typography data-test-id='NavigationMenuCartIconTypography' scale='p3'>
          {!isBagEmpty ? linesCount : null}
        </Typography>
      </Flex>
    );
  };
  const cartLink = isProcessing ? (
    <CartIcon />
  ) : (
    <Box
      data-test-id='NavigationMenuCartLink'
      onClick={(evt) => {
        if (isProcessing) {
          evt.preventDefault();
        }

        handleCartClick && handleCartClick();
      }}
      style={{ textDecoration: 'none' }}
    >
      <CartIcon />
    </Box>
  );

  const handleSearchClick = () => {
    if (asPath?.includes('/search/')) {
      return;
    }
    searchStateActions.toggleOverlayStatus();
  };
  return (
    <>
      <S.NavicationMenu data-test-id='NavigationMenu'>
        <li>
          <S.Link
            className='BreitlingWidgetTrigger'
            href='#'
            rel='nofollow noreferrer noopener'
            title={formatMessage(commonMessages.accessibilityFeatures)}
          >
            <S.AccessibilityIcon
              $headerBackground={headerBackground}
              name='accessibility'
            />
          </S.Link>
        </li>
        <li
          className='navigationmenu-search'
          data-test-id='NavigationMenuSearch'
        >
          <S.SearchIcon
            $headerBackground={headerBackground}
            $isShrunk={isShrunk}
            $textColorOption={textColorOption}
            data-test-id='NavigationMenuSearchIcon'
            data-testid='NavigationMenuSearchIcon'
            name='search'
            onClick={handleSearchClick}
          />
        </li>
        <li data-test-id='NavigationMenuWishlist'>
          <LinkWithLocale href='/my-account/my-wishlist/' passHref>
            <S.HeartOutline
              $headerBackground={headerBackground}
              $isHeightLight={isHighlighted}
              $isShrunk={isShrunk}
              $textColorOption={textColorOption}
              data-test-id='NavigationMenuWishlistIcon'
              name='heartOutline'
            />
          </LinkWithLocale>
        </li>
        <li data-test-id='NavigationMenuAccount'>
          <LinkWithLocale
            href={
              isAuthenticated
                ? paths.myAccount.asPath()
                : paths.myAccount.login.url({
                    query: { next: paths.myAccount.asPath() },
                  })
            }
            passHref
          >
            <S.AccountIcon
              $headerBackground={headerBackground}
              $isShrunk={isShrunk}
              $textColorOption={textColorOption}
              data-test-id='NavigationMenuAccountIcon'
              name='account'
            />
          </LinkWithLocale>
        </li>
        {!productCatalogMode && (
          <li data-test-id='NavigationMenuCartLinkContainer'>{cartLink}</li>
        )}
      </S.NavicationMenu>
    </>
  );
};

NavigationMenu.displayName = 'NavigationMenu';
